import { Link } from 'react-router';
import { Page } from '../components/AppShell';
import { SolvingSettings, type SetSetting } from '../components/SettingsParts';
import { Card, ScreenHeader, SectionTitle, Segmented } from '../components/ui';
import { isStandalone, useInstallPrompt } from '../lib/install';
import { useMe, useSaveSettings } from '../lib/queries';

/** Personal preferences, plus Stats (off Home since 2026-11-01, Marko's choice);
 * everything about running the app lives in Admin. */
export function SettingsPage() {
  const me = useMe();
  const save = useSaveSettings();
  const install = useInstallPrompt();
  if (!me.data) return null;
  const s = me.data.settings;
  const set: SetSetting = (key) => (value) => save.mutate({ [key]: value });

  return (
    <>
      <ScreenHeader title="Settings" />
      <Page>
        <section className="flex flex-col gap-2">
          <SectionTitle>Digits</SectionTitle>
          <Segmented
            label="Digits"
            value={s.digits}
            onChange={set('digits')}
            options={[
              { value: '1-9', label: '1–9' },
              { value: '0-9', label: '0–9' },
              { value: 'letters', label: 'Letters' },
            ]}
          />
          <p className="mx-1 my-0 text-[12px] text-muted">
            Puzzles that use 0 or letters switch over on their own.
          </p>
        </section>

        <section className="flex flex-col gap-2">
          <SectionTitle>Default mode</SectionTitle>
          <Segmented
            label="Default mode"
            value={s.defaultMode}
            onChange={set('defaultMode')}
            options={[
              { value: 'sudokupad', label: 'SudokuPad' },
              { value: 'simple', label: 'Simple' },
            ]}
          />
        </section>

        <SolvingSettings s={s} set={set} />
        {save.isError && (
          <p role="alert" className="mx-1 my-0 text-[13px] text-mistake">
            That change didn’t save. Check your connection and try again.
          </p>
        )}

        <section className="flex flex-col gap-2">
          <SectionTitle>More</SectionTitle>
          <Card className="flex flex-col items-start gap-3 px-3.5 py-3.5">
            <Link to="/stats/me" className="text-[14px] font-semibold">
              Stats
            </Link>
          </Card>
        </section>

        <section className="flex flex-col gap-2">
          <SectionTitle>App</SectionTitle>
          <Card className="flex flex-col gap-3 px-3.5 py-3.5 text-[15px]">
            <div className="flex items-center gap-3">
              <span className="flex-1">
                Logged in as <strong>{me.data.user.displayName}</strong>
              </span>
              <form method="post" action="/auth/logout" className="m-0">
                <button
                  type="submit"
                  className="h-11 rounded-btn border border-line bg-surface px-4 text-[14px] font-semibold text-ink"
                >
                  Log out
                </button>
              </form>
            </div>
            {!isStandalone() &&
              (install.canPrompt ? (
                <button
                  type="button"
                  onClick={() => void install.install()}
                  className="h-11 rounded-btn border-0 bg-accent text-[15px] font-bold text-white"
                >
                  Install ZincSudoku
                </button>
              ) : (
                <p className="m-0 text-[13px] text-muted">
                  To install: in Chrome or Edge use “Install app” in the address bar or menu; on
                  iPhone or iPad use Share → Add to Home Screen.
                </p>
              ))}
            {me.data.user.role === 'admin' && (
              <Link to="/admin" className="text-[14px] font-semibold">
                Open Admin
              </Link>
            )}
          </Card>
        </section>
      </Page>
    </>
  );
}
